'use client'

import { motion } from 'framer-motion'
import { Phone } from 'lucide-react'
import { WhatsAppIcon } from './WhatsAppIcon'
import { getWhatsAppUrl, PHONE_CALL_URL, WHATSAPP_PHONE } from '@/lib/whatsapp'

export function FloatingWidget() {
  const whatsappUrl = getWhatsAppUrl({ type: 'consultation' })

  return (
    <>
      {/* Desktop Floating Widget (lg and up) */}
      <aside aria-label="Quick contact" className="hidden lg:flex fixed right-6 bottom-6 z-50 flex-col items-end gap-2">
        <motion.a
          id="desktop-floating-whatsapp-btn"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
          className="group relative flex items-center gap-3 px-5 py-3.5 rounded-full text-white font-bold text-sm shadow-2xl transition-all duration-300"
          style={{
            backgroundColor: '#25D366',
            boxShadow: '0 8px 24px -4px rgba(37, 211, 102, 0.45), 0 4px 12px rgba(0, 0, 0, 0.12)',
          }}
          title="Chat with our specialist on WhatsApp (Online)"
          aria-label="Chat with our specialist on WhatsApp"
        >
          {/* Live pulsing online dot */}
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
          </span>

          <WhatsAppIcon className="w-5 h-5 fill-white" />

          <span className="tracking-wide">Chat on WhatsApp</span>

          {/* Micro status chip */}
          <span className="text-[11px] font-semibold bg-black/15 px-2 py-0.5 rounded-full tracking-normal">
            Online
          </span>
        </motion.a>
      </aside>

      {/* Mobile Sticky Bottom Bar (< lg) */}
      <aside
        aria-label="Mobile quick contact actions"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-[#DBEAFE]"
        style={{ boxShadow: '0 -6px 25px rgba(15, 23, 42, 0.12)' }}
      >
        {/* Top Micro-Status Reassurance Indicator */}
        <div className="bg-[#EFF6FF] border-b border-[#DBEAFE] py-1 px-3 text-center flex items-center justify-center gap-2 text-[11px] font-semibold text-[#1E3A8A]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
          </span>
          <span>Advisors Online</span>
          <span className="text-slate-300">•</span>
          <span className="text-[#059669]">⚡ Typical Reply: ~5 Mins</span>
        </div>

        {/* Action Buttons Row */}
        <div className="p-2.5 grid grid-cols-2 gap-2.5 max-w-md mx-auto">
          {/* 1. Chat on WhatsApp */}
          <a
            id="mobile-sticky-whatsapp-btn"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-white font-bold text-xs tracking-wide shadow-md transition-all active:scale-98"
            style={{
              backgroundColor: '#25D366',
              boxShadow: '0 4px 12px rgba(37, 211, 102, 0.35)',
            }}
            aria-label="Chat on WhatsApp"
          >
            <WhatsAppIcon className="w-4 h-4 fill-white flex-shrink-0" />
            <span className="truncate">Chat WhatsApp</span>
          </a>

          {/* 2. Call Now */}
          <a
            id="mobile-sticky-call-btn"
            href={PHONE_CALL_URL}
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-white font-bold text-xs tracking-wide transition-all active:scale-98"
            style={{
              backgroundColor: '#0F172A',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25)',
            }}
            aria-label={`Call ${WHATSAPP_PHONE}`}
          >
            <Phone className="w-4 h-4 flex-shrink-0 text-[#60A5FA]" />
            <span className="truncate">Call Now</span>
          </a>
        </div>
      </aside>

      {/* Spacing buffer to prevent sticky bottom bar from covering content */}
      <div className="lg:hidden h-24 pointer-events-none" aria-hidden="true" />
    </>
  )
}
